import { ExtendedNestedSubAccount } from '../OrgChartContainer';
import { Box, Card } from 'vopay';
import { Button, Title, CircleButton } from '@/components/UI';
import useSubAccountCreationFormConfig from './useSubAccountCreationFormConfig';
import useSubAccounts from '@/hooks/useApi/useSubAccounts';
import useDataFetchIndicator from '@/hooks/useDataFetchIndicator';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useTranslation } from 'react-i18next';

interface CreateSubAccountProps {
  node: ExtendedNestedSubAccount;
  onClose: () => void;
  onSuccess: () => void;
}

export default function CreateSubAccount({ node, onClose, onSuccess }: CreateSubAccountProps) {
  const [, setSubmitting] = useDataFetchIndicator();
  const { createSubAccount } = useSubAccounts();
  const alertDialog = useAlertDialog();
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.Create' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const { renderFormElements, ctx } = useSubAccountCreationFormConfig({
    bearer: node.Token,
    onSubmit: async (serializedData) => {
      try {
        setSubmitting(true);

        await createSubAccount({
          ...serializedData,
          ParentAccountID: node.AccountID,
        });

        onSuccess();
      } catch (err) {
        const msg =
          err instanceof Error && err.message
            ? err.message
            : t('Form.creationErrorMessage', { defaultValue: 'Sub-Account Creation Failed' });

        alertDialog.error(tGlobal('error', { defaultValue: 'Error' }), msg);
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Card hoverEffect={false} style={{ padding: '32px', backgroundColor: 'var(--vopayWhite)' }}>
      <div style={{ minWidth: '600px' }}>
        <Box flx="col" gapSize="24px">
          <Box flx="col" gapSize="15px">
            <Box jc="fe">
              <CircleButton onClick={onClose} icon="close" />
            </Box>

            <Title level="4">{t('title')}</Title>
          </Box>

          {renderFormElements()}

          <Button
            id="sub-account-creation-btn"
            onClick={() => {
              ctx.handleSubmit();
            }}
            disabled={!ctx.isValid}
            colorScheme="primary"
          >
            {t('Form.submitButton')}
          </Button>
        </Box>
      </div>
    </Card>
  );
}
